// The note editor that sits at the top of every History / Activity tab.
//
// One box, no separate input: a single line when empty, growing to fit
// what is typed. Bold, italic and lists come from the toolbar, from ⌘B /
// ⌘I, or from typing "- " or "1. " at the start of a line. The parent
// slideover binds `markdown` with x-model (through x-modelable) and posts
// it as the note's content; the editor turns its own DOM into Markdown
// on every keystroke so the parent never sees HTML. When the parent
// clears the bound value after a save, the editor empties itself.

import { htmlToMarkdown } from '../utils/note_markdown'

const EMPTY = '<p><br></p>'

const TYPE_ICONS = {
  Property: 'ph-house-line',
  Tenancy: 'ph-file-text',
  Landlord: 'ph-user-circle',
  Person: 'ph-user',
  Contractor: 'ph-wrench',
  Job: 'ph-hammer',
  EmailThread: 'ph-envelope-simple',
  TenancyApplication: 'ph-clipboard-text',
  PropertyKeyBunch: 'ph-key',
  CertificateType: 'ph-certificate'
}

const TYPE_WORDS = {
  Property: 'property',
  Tenancy: 'tenancy',
  Landlord: 'landlord',
  Person: 'person',
  Contractor: 'contractor',
  Job: 'job',
  EmailThread: 'email',
  TenancyApplication: 'application',
  PropertyKeyBunch: 'key bunch',
  CertificateType: 'certificate type'
}

export function noteEditor({ placeholder = 'Log a call, note, or update…', autofocus = false, subject = null, links = null } = {}) {
  return {
    markdown: '',
    lastSynced: '',
    placeholder,
    autofocus,
    engaged: false,

    // Linking: `subjectFn` returns the record being written on (live, from
    // the parent's state); `links` is the parent's array of picked links.
    subjectFn: subject,
    links: links || [],
    linkMenuOpen: false,
    linkOptions: { groups: [] },
    linkOptionsLoaded: false,
    loadingLinks: false,
    linksTouched: false,
    active: { bold: false, italic: false, insertUnorderedList: false, insertOrderedList: false },

    init() {
      try { document.execCommand('defaultParagraphSeparator', false, 'p') } catch (_) { /* not every browser */ }
      this.ensureBlock()
      if (this.autofocus) {
        this.$nextTick(() => {
          this.editor.focus({ preventScroll: true })
          this.editor.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
      }
      this.$watch('markdown', (value) => {
        // The parent emptied the field (saved, or reset) — mirror it in the DOM.
        // A value the editor itself just synced is not an external clear.
        if (value === '' && this.lastSynced !== '') this.reset()
      })
    },

    get blank() {
      return this.markdown === ''
    },

    get subject() {
      return this.subjectFn ? this.subjectFn() : null
    },

    get subjectLabel() {
      if (this.subject?.label) return this.subject.label
      const type = this.subject?.type
      return type ? `This ${TYPE_WORDS[type] || type.toLowerCase()}` : 'Here'
    },

    get linksRowVisible() {
      return !!this.subjectFn && (this.engaged || !this.blank || this.links.length > 0)
    },

    iconFor(type) {
      return TYPE_ICONS[type] || 'ph-link'
    },

    onFocus() {
      this.engaged = true
      this.loadLinkOptions()
    },

    async loadLinkOptions() {
      if (!this.subjectFn || this.linkOptionsLoaded || this.loadingLinks) return
      const subject = this.subject
      if (!subject?.id) return
      this.loadingLinks = true
      try {
        const params = new URLSearchParams({ subject_type: subject.type, subject_id: subject.id })
        const response = await fetch(`/notes/link_options?${params}`, { headers: { 'Accept': 'application/json' } })
        if (!response.ok) throw new Error('Could not load link options')
        this.linkOptions = await response.json()
        this.linkOptionsLoaded = true
      } catch (error) {
        console.error('Failed to load link options:', error)
      } finally {
        this.loadingLinks = false
      }
    },

    isLinked(item) {
      return this.links.some((l) => l.type === item.type && String(l.id) === String(item.id))
    },

    toggleLink(item) {
      this.linksTouched = true
      if (this.isLinked(item)) this.removeLink(item)
      else this.links.push({ type: item.type, id: item.id, label: item.label })
    },

    removeLink(item) {
      this.linksTouched = true
      const index = this.links.findIndex((l) => l.type === item.type && String(l.id) === String(item.id))
      if (index >= 0) this.links.splice(index, 1)
    },

    toggleLinkMenu() {
      this.engaged = true
      this.linkMenuOpen = !this.linkMenuOpen
      if (this.linkMenuOpen) this.loadLinkOptions()
    },

    // After a save the parent has emptied the links; the next note starts with none
    resetLinks() {
      this.linksTouched = false
      this.linkMenuOpen = false
      this.links.splice(0, this.links.length)
    },

    get editor() {
      return this.$refs.editor
    },

    ensureBlock() {
      if (!this.editor.firstElementChild) this.editor.innerHTML = EMPTY
    },

    editorHasContent() {
      return this.editor.textContent.replace(/ /g, ' ').trim() !== '' || !!this.editor.querySelector('li')
    },

    sync() {
      this.ensureBlock()
      const markdown = this.editorHasContent() ? htmlToMarkdown(this.editor) : ''
      this.lastSynced = markdown
      this.markdown = markdown
      this.refreshActive()
    },

    reset() {
      this.editor.innerHTML = EMPTY
      this.lastSynced = ''
      this.markdown = ''
      this.engaged = false
      this.resetLinks()
      this.refreshActive()
    },

    // Cancel: discard what was typed
    clear() {
      this.reset()
      this.editor.blur()
    },

    focus() {
      this.editor.focus()
    },

    exec(command) {
      this.editor.focus()
      document.execCommand(command, false, null)
      this.sync()
    },

    refreshActive() {
      Object.keys(this.active).forEach((command) => {
        let on = false
        try { on = document.queryCommandState(command) } catch (_) { on = false }
        this.active[command] = !!on
      })
    },

    onKeydown(event) {
      const mod = event.metaKey || event.ctrlKey
      const key = event.key.toLowerCase()

      if (mod && key === 'b') { event.preventDefault(); this.exec('bold'); return }
      if (mod && key === 'i') { event.preventDefault(); this.exec('italic'); return }
      if (event.key === 'Escape') { event.preventDefault(); this.linkMenuOpen = false; if (this.blank) this.engaged = false; this.editor.blur(); return }
      if (event.key === ' ') this.maybeStartList(event)
    },

    // "- " or "1. " at the start of a line becomes a list, as in most editors.
    // The line is swapped for a list directly rather than through
    // execCommand, which refuses to start a list in a freshly emptied
    // paragraph (the first line of a new note).
    maybeStartList(event) {
      const selection = window.getSelection()
      const node = selection && selection.anchorNode
      if (!node || node.nodeType !== Node.TEXT_NODE) return
      if (node.parentElement && node.parentElement.closest('li')) return

      const before = node.textContent.slice(0, selection.anchorOffset)
      const tag = (before === '-' || before === '*') ? 'ul' : before === '1.' ? 'ol' : null
      if (!tag) return

      // The block this line lives in: the child of the editor that contains the caret
      let block = node
      while (block.parentNode && block.parentNode !== this.editor) block = block.parentNode
      if (block.parentNode !== this.editor) return
      // Only when the marker is all that has been typed on the line
      if (block.textContent.replace(/ /g, ' ').trim() !== before) return

      event.preventDefault()
      const list = document.createElement(tag)
      const item = document.createElement('li')
      item.innerHTML = '<br>'
      list.appendChild(item)
      block.replaceWith(list)

      const range = document.createRange()
      range.setStart(item, 0)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)
      this.sync()
    },

    // Pasted text comes in as text, never as someone else's markup.
    onPaste(event) {
      event.preventDefault()
      const text = (event.clipboardData || window.clipboardData).getData('text/plain')
      document.execCommand('insertText', false, text)
      this.sync()
    }
  }
}
